// 共享「数据详情」弹窗：三列（客户数据 / 兼职截图 / 时间线），供 数据核对(H3) 与 对账结算(H5) 复用
import React, { useEffect, useState } from 'react';
import { Dialog, Descriptions } from 'tdesign-react';

// 处理状态中文（与全局一致）
const STATUS_LABEL = {
  UNASSIGNED: '未分配', UNSUBMITTED: '未分配', PRE_ASSIGNED: '已分配未发送', SENT: '已发送未领取',
  CLAIMED: '已领取进行中', SUBMITTED: '已截图待审核', PASSED: '截图审核通过', FAILED: '截图审核不通过',
  RECALLED: '分配后撤回', REJECTED: '分配后拒绝',
};
const SETTLE_LABEL = { UNREVIEWED: '未对账', SETTLEMENTING: '对账中', CONFIRMED: '对账通过', DISAGREED: '对账未通过' };

const fmtDt = (d) => (d ? String(d).replace('T', ' ').slice(0, 19) : '');

// 组装时间线节点：数据上传时间 + 分配历史 + 审批流水 + 截图上传时间，按时间排序
const buildTimeline = (d) => {
  const nm = d.assignName || '';
  const list = [];
  if (d.createTime) list.push({ time: d.createTime, text: '上传数据' });
  const assignMap = {
    ASSIGN_PRE: nm ? `分配给${nm}` : '分配',
    ASSIGN_SEND: '发送给兼职',
    RECALL: '已撤回',
    REJECT: nm ? `${nm}拒绝` : '拒绝',
  };
  (d.assignLogs || []).forEach(l => list.push({ time: l.time, text: assignMap[l.action] || l.action || l.operatorRole, reason: l.reason }));
  const bizMap = {
    CLAIM: nm ? `${nm}已领取` : '已领取',
    REJECT: nm ? `${nm}拒绝` : '拒绝',
    SUBMIT: nm ? `${nm}提交审核（已截图待审核）` : '提交审核（已截图待审核）',
    WITHDRAW_SUBMIT: '撤回提交',
    RESUBMIT: '补传重提',
    PASS: '截图审核通过',
    FAIL: '截图审核不通过',
    SETTLE: '生成对账函（对账中）',
    SETTLE_CONFIRM: '对账通过',
    SETTLE_DISAGREE: '对账不通过',
    SETTLE_WITHDRAW: '客服撤回对账',
  };
  (d.bizLogs || []).forEach(l => list.push({ time: l.time, text: bizMap[l.action] || l.action || l.operatorRole, reason: l.reason }));
  (d.images || []).forEach(im => {
    if (im.type === 'ADD') list.push({ time: im.time, text: nm ? `${nm}提交上传添加截图` : '上传添加截图' });
    else if (im.type === 'GROUP') list.push({ time: im.time, text: nm ? `${nm}上传进群截图` : '上传进群截图' });
  });
  return list.filter(x => x.time).sort((a, b) => String(a.time).localeCompare(String(b.time)) || 0);
};

/**
 * 数据详情弹窗（三列）：
 * 第一列 客户数据（业主/手机/微信/城市/小区/兼职 + 话术 + 群二维码 + 备注 + 状态）
 * 第二列 兼职上传截图（添加/进群分开，点击全屏预览）
 * 第三列 时间线（上传/分配/发送/撤回/领取/拒绝/截图/提交/审核/对账 全节点）
 */
export default function ParttimeDataDetailDialog({ row, onClose }) {
  const [viewImg, setViewImg] = useState(null);

  // ESC 关闭图片全屏预览
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setViewImg(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <Dialog header="数据详情" visible={!!row} footer={null} width="1120px" closeOnOverlayClick onClose={onClose}>
        {row && (
          <div style={{ display: 'flex', gap: 24, maxHeight: '60vh', overflow: 'hidden' }}>
            {/* 第一列：客户数据 */}
            <div style={{ flex: '1 1 40%', minWidth: 0, overflow: 'auto', paddingRight: 4 }}>
              <p style={{ fontWeight: 600, margin: '0 0 10px' }}>客户数据</p>
              <Descriptions
                column={1}
                items={[
                  { label: '业主姓名', content: row.ownerName || '' },
                  { label: '手机号', content: row.phone || '' },
                  { label: '微信号', content: row.wechat || '' },
                  { label: '城市', content: row.city || '' },
                  { label: '小区', content: row.community || '' },
                  { label: '兼职人员', content: row.assignName || '' },
                ]}
              />
              <div style={{ marginTop: 12 }}>
                <span style={{ color: '#666' }}>话术：</span>
                <div style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap', marginTop: 4 }}>{row.script || ''}</div>
              </div>
              <div style={{ marginTop: 12 }}>
                <span style={{ color: '#666' }}>群二维码：</span>
                {(row.qrcodeList && row.qrcodeList.length)
                  ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                      {row.qrcodeList.map((q, i) => (
                        <img key={i} src={q.url} alt="群二维码" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }} onClick={() => setViewImg(q.url)} />
                      ))}
                    </div>
                  : ''}
              </div>
              <Descriptions
                column={1}
                items={[
                  { label: '群二维码备注', content: row.qrcodeRemark || '' },
                  { label: '处理状态', content: STATUS_LABEL[row.status] || row.status || '' },
                  { label: '对账状态', content: SETTLE_LABEL[row.settleStatus] || row.settleStatus || '' },
                ]}
              />
            </div>

            {/* 第二列：兼职上传截图（添加/进群分开） */}
            <div style={{ flex: '1 1 28%', minWidth: 0, overflow: 'auto', paddingRight: 4 }}>
              <p style={{ fontWeight: 600, margin: '0 0 10px' }}>兼职截图</p>
              <div style={{ marginBottom: 16 }}>
                <span style={{ color: '#666', fontWeight: 600 }}>添加截图</span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                  {(row.images || []).filter(im => im.type === 'ADD').map((im, i) => (
                    <img key={i} src={im.url} alt="添加截图" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }} onClick={() => setViewImg(im.url)} />
                  ))}
                  {(row.images || []).filter(im => im.type === 'ADD').length === 0 && <span style={{ color: '#bbb' }}>暂无</span>}
                </div>
              </div>
              <div>
                <span style={{ color: '#666', fontWeight: 600 }}>进群截图</span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                  {(row.images || []).filter(im => im.type === 'GROUP').map((im, i) => (
                    <img key={i} src={im.url} alt="进群截图" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }} onClick={() => setViewImg(im.url)} />
                  ))}
                  {(row.images || []).filter(im => im.type === 'GROUP').length === 0 && <span style={{ color: '#bbb' }}>暂无</span>}
                </div>
              </div>
            </div>

            {/* 第三列：时间线 */}
            <div style={{ flex: '1 1 32%', minWidth: 0, overflow: 'auto' }}>
              <p style={{ fontWeight: 600, margin: '0 0 10px' }}>时间线</p>
              {buildTimeline(row).map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px dashed #f0f0f0' }}>
                  <span style={{ color: '#888', whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDt(l.time)}</span>
                  <span style={{ fontSize: 12 }}>{l.text}</span>
                  {l.reason && <span style={{ color: '#d54941', fontSize: 12 }}>原因：{l.reason}</span>}
                </div>
              ))}
              {buildTimeline(row).length === 0 && <span style={{ color: '#bbb' }}>暂无记录</span>}
            </div>
          </div>
        )}
      </Dialog>

      {/* 图片全屏预览（自定义：遮罩 + 图片，点击空白 / ESC 关闭） */}
      {viewImg && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
          onClick={() => setViewImg(null)}
        >
          <img src={viewImg} alt="预览" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 6 }} />
        </div>
      )}
    </>
  );
}
